import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { transportAdminAPI } from '../../services/api';

const ROLE_LABEL = { owner: 'مالك', dispatcher: 'منسّق', driver: 'سائق' };
const TYPE_LABEL = {
  individual_driver: 'سائق فردي', vehicle_owner: 'مالك مركبة', transport_office: 'مكتب نقل',
  transport_company: 'شركة نقل', logistics_company: 'شركة لوجستية', fleet_owner: 'مالك أسطول',
};

const inputCls = 'w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400';
const labelCls = 'block text-xs text-gray-600 mb-1';

export default function SuperTransportProvidersPage() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filter, setFilter]       = useState('');

  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating]     = useState(false);
  const [form, setForm] = useState({ name: '', providerType: 'individual_driver', ownerName: '', city: '', address: '', phone: '', email: '', notes: '' });

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [staff, setStaff]   = useState([]);

  const [showStaff, setShowStaff] = useState(false);
  const [staffForm, setStaffForm] = useState({ name: '', email: '', phone: '', password: '', role: 'owner' });
  const [staffSaving, setStaffSaving] = useState(false);

  const [commissionInput, setCommissionInput] = useState('');
  const [savingCommission, setSavingCommission] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter) params.isActive = filter === 'active';
      const res = await transportAdminAPI.list(params);
      setProviders(res.data.data || []);
    } catch { toast.error('خطأ في تحميل المزودين'); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    setDetailLoading(true);
    setDetail({ id });
    try {
      const [pRes, sRes] = await Promise.all([
        transportAdminAPI.get(id),
        transportAdminAPI.listStaff(id).catch(() => ({ data: { data: [] } })),
      ]);
      setDetail(pRes.data.data);
      setStaff(sRes.data.data || []);
      setCommissionInput(String(pRes.data.data.commissionRate));
    } catch { toast.error('خطأ في تحميل بيانات المزود'); }
    finally { setDetailLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) return toast.error('الاسم والهاتف مطلوبان');
    setCreating(true);
    try {
      await transportAdminAPI.create(form);
      toast.success('تم إنشاء المزود. يمكنك تفعيله الآن.');
      setShowCreate(false);
      setForm({ name: '', providerType: 'individual_driver', ownerName: '', city: '', address: '', phone: '', email: '', notes: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'خطأ في الإنشاء'); }
    finally { setCreating(false); }
  };

  const handleApprove = async (id) => {
    try {
      await transportAdminAPI.approve(id);
      toast.success('تم تفعيل المزود');
      load();
      if (detail?.id === id) openDetail(id);
    } catch (e) { toast.error(e.response?.data?.message || 'خطأ'); }
  };

  const handleSuspend = async (id) => {
    if (!window.confirm('هل أنت متأكد من إيقاف هذا المزود؟')) return;
    try {
      await transportAdminAPI.suspend(id);
      toast.success('تم إيقاف المزود');
      load();
      if (detail?.id === id) openDetail(id);
    } catch (e) { toast.error(e.response?.data?.message || 'خطأ'); }
  };

  const handleSaveCommission = async () => {
    const rate = parseFloat(commissionInput);
    if (isNaN(rate) || rate < 0 || rate > 100) return toast.error('نسبة غير صحيحة (0-100)');
    setSavingCommission(true);
    try {
      await transportAdminAPI.setCommission(detail.id, { commissionRate: rate });
      toast.success('تم تحديث نسبة العمولة');
      openDetail(detail.id);
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'خطأ'); }
    finally { setSavingCommission(false); }
  };

  const handleAddStaff = async (e) => {
    e.preventDefault();
    setStaffSaving(true);
    try {
      await transportAdminAPI.addStaff(detail.id, staffForm);
      toast.success('تم إضافة الموظف بنجاح');
      setShowStaff(false);
      setStaffForm({ name: '', email: '', phone: '', password: '', role: 'owner' });
      openDetail(detail.id);
    } catch (err) { toast.error(err.response?.data?.message || 'خطأ في الإضافة'); }
    finally { setStaffSaving(false); }
  };

  const handleToggleStaff = async (staffId) => {
    try {
      await transportAdminAPI.toggleStaff(detail.id, staffId);
      toast.success('تم تغيير حالة الموظف');
      openDetail(detail.id);
    } catch { toast.error('خطأ'); }
  };

  return (
    <div dir="rtl" className="p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">🚚 مزودو النقل</h1>
          <p className="text-sm text-gray-500 mt-1">سوق نقل البضائع — إدارة مزودي الخدمة الشركاء</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition">
          + إضافة مزود
        </button>
      </div>

      <div className="flex flex-wrap gap-3 mb-5">
        <select className="border rounded-lg px-3 py-2 text-sm focus:outline-none" value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="">الكل</option>
          <option value="active">نشط</option>
          <option value="inactive">موقوف / معلق</option>
        </select>
      </div>

      {loading ? (
        <div className="text-center py-16 text-gray-400">جاري التحميل...</div>
      ) : providers.length === 0 ? (
        <div className="text-center py-16 text-gray-400">لا يوجد مزودون</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="px-4 py-3 text-right">المزود</th>
                <th className="px-4 py-3 text-right">النوع</th>
                <th className="px-4 py-3 text-right">المدينة</th>
                <th className="px-4 py-3 text-right">الهاتف</th>
                <th className="px-4 py-3 text-right">العمولة</th>
                <th className="px-4 py-3 text-right">الحالة</th>
                <th className="px-4 py-3 text-right">إجراءات</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {providers.map((p) => (
                <tr key={p.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold text-gray-900">{p.name}</td>
                  <td className="px-4 py-3 text-gray-600">{TYPE_LABEL[p.providerType] || p.providerType}</td>
                  <td className="px-4 py-3 text-gray-600">{p.city || '—'}</td>
                  <td className="px-4 py-3 text-gray-600" dir="ltr">{p.phone}</td>
                  <td className="px-4 py-3 text-gray-700">{p.commissionRate}%</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${p.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {p.isActive ? 'نشط' : 'معلق'}
                    </span>
                  </td>
                  <td className="px-4 py-3 flex gap-2">
                    <button onClick={() => openDetail(p.id)} className="text-blue-600 hover:underline text-xs font-semibold">تفاصيل</button>
                    {!p.isActive
                      ? <button onClick={() => handleApprove(p.id)} className="text-green-600 hover:underline text-xs font-semibold">تفعيل</button>
                      : <button onClick={() => handleSuspend(p.id)} className="text-red-500 hover:underline text-xs font-semibold">إيقاف</button>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-2xl">
            <h2 className="text-lg font-bold mb-5">إضافة مزود نقل جديد</h2>
            <form onSubmit={handleCreate} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>اسم المزود *</label><input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
                <div>
                  <label className={labelCls}>نوع المزود</label>
                  <select className={inputCls} value={form.providerType} onChange={(e) => setForm({ ...form, providerType: e.target.value })}>
                    {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div><label className={labelCls}>اسم المسؤول</label><input className={inputCls} value={form.ownerName} onChange={(e) => setForm({ ...form, ownerName: e.target.value })} /></div>
                <div><label className={labelCls}>المدينة</label><input className={inputCls} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
                <div><label className={labelCls}>الهاتف *</label><input dir="ltr" className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required /></div>
                <div><label className={labelCls}>البريد الإلكتروني</label><input type="email" dir="ltr" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              </div>
              <div><label className={labelCls}>العنوان</label><input className={inputCls} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
              <div><label className={labelCls}>ملاحظات داخلية</label><textarea rows={2} className={inputCls} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={creating} className="flex-1 bg-blue-600 text-white py-2 rounded-lg font-semibold text-sm hover:bg-blue-700 disabled:opacity-50 transition">
                  {creating ? 'جاري الإنشاء...' : 'إنشاء المزود'}
                </button>
                <button type="button" onClick={() => setShowCreate(false)} className="flex-1 border py-2 rounded-lg font-semibold text-sm text-gray-600 hover:bg-gray-50 transition">إلغاء</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Drawer */}
      {detail && (
        <div className="fixed inset-0 bg-black/50 flex items-end lg:items-center justify-center z-50 p-0 lg:p-4">
          <div className="bg-white rounded-t-2xl lg:rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
            {detailLoading ? (
              <div className="p-10 text-center text-gray-400">جاري التحميل...</div>
            ) : (
              <div className="p-6">
                <div className="flex items-start justify-between mb-5">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">{detail.name}</h2>
                    <p className="text-sm text-gray-500">{TYPE_LABEL[detail.providerType]} — {detail.city}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${detail.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {detail.isActive ? 'نشط' : 'معلق'}
                    </span>
                    <button onClick={() => setDetail(null)} className="text-gray-400 hover:text-gray-600 p-1">✕</button>
                  </div>
                </div>

                {/* Verification info — what an admin actually needs to decide
                    whether to approve: what they registered for, contact
                    details, and the documents they uploaded. */}
                <div className="mb-5 flex flex-wrap gap-2">
                  {detail.servicesCargo && <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-100">📦 نقل بضائع</span>}
                  {detail.servicesRides && <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-100">🚕 مشاوير</span>}
                </div>
                <div className="mb-5 bg-gray-50 rounded-xl p-3 text-sm space-y-1">
                  <p className="text-gray-700"><span className="text-gray-500">الهاتف: </span><span dir="ltr">{detail.phone}</span></p>
                  {detail.email && <p className="text-gray-700"><span className="text-gray-500">البريد: </span><span dir="ltr">{detail.email}</span></p>}
                  {detail.address && <p className="text-gray-700"><span className="text-gray-500">العنوان: </span>{detail.address}</p>}
                  <div className="flex gap-4 pt-1">
                    {detail.idDocumentUrl
                      ? <a href={detail.idDocumentUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline font-semibold">📄 عرض صورة الهوية</a>
                      : <span className="text-xs text-red-500 font-semibold">⚠️ لم يتم رفع صورة الهوية</span>}
                    {detail.crDocumentUrl && (
                      <a href={detail.crDocumentUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline font-semibold">📄 عرض السجل التجاري</a>
                    )}
                  </div>
                </div>

                <div className="flex gap-2 mb-5">
                  {!detail.isActive
                    ? <button onClick={() => handleApprove(detail.id)} className="flex-1 bg-green-600 text-white py-2 rounded-lg text-sm font-semibold hover:bg-green-700 transition">تفعيل المزود</button>
                    : <button onClick={() => handleSuspend(detail.id)} className="flex-1 bg-red-500 text-white py-2 rounded-lg text-sm font-semibold hover:bg-red-600 transition">إيقاف المزود</button>
                  }
                  <button onClick={() => setShowStaff(true)} className="flex-1 bg-blue-600 text-white py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition">+ إضافة موظف</button>
                </div>

                <div className="mb-5 bg-gray-50 rounded-xl p-3 flex items-center gap-3">
                  <label className="text-xs font-bold text-gray-600 flex-shrink-0">نسبة العمولة %</label>
                  <input type="number" min="0" max="100" step="0.5" className="border rounded-lg px-3 py-1.5 text-sm w-24" value={commissionInput} onChange={(e) => setCommissionInput(e.target.value)} />
                  <button onClick={handleSaveCommission} disabled={savingCommission} className="text-xs font-bold text-blue-700 hover:underline disabled:opacity-50">
                    {savingCommission ? '...جارٍ الحفظ' : 'حفظ'}
                  </button>
                </div>

                <h3 className="font-bold text-gray-700 mb-3 text-sm">الموظفون</h3>
                {staff.length === 0 ? (
                  <p className="text-sm text-gray-400">لا يوجد موظفون مضافون بعد</p>
                ) : (
                  <div className="space-y-2">
                    {staff.map((s) => (
                      <div key={s.id} className="flex items-center justify-between bg-gray-50 rounded-xl p-3">
                        <div>
                          <p className="font-semibold text-sm text-gray-800">{s.user?.name}</p>
                          <p className="text-xs text-gray-500">{s.user?.email} · {ROLE_LABEL[s.role]}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${s.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-500'}`}>
                            {s.isActive ? 'نشط' : 'موقوف'}
                          </span>
                          <button onClick={() => handleToggleStaff(s.id)} className="text-xs text-gray-500 hover:text-gray-800 underline">
                            {s.isActive ? 'إيقاف' : 'تفعيل'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add Staff Modal */}
      {showStaff && detail && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl">
            <h2 className="text-lg font-bold mb-5">إضافة موظف — {detail.name}</h2>
            <form onSubmit={handleAddStaff} className="space-y-3">
              <div><label className={labelCls}>الاسم الكامل *</label><input className={inputCls} value={staffForm.name} onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })} required /></div>
              <div><label className={labelCls}>البريد الإلكتروني *</label><input type="email" dir="ltr" className={inputCls} value={staffForm.email} onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })} required /></div>
              <div><label className={labelCls}>رقم الهاتف *</label><input dir="ltr" className={inputCls} value={staffForm.phone} onChange={(e) => setStaffForm({ ...staffForm, phone: e.target.value })} required /></div>
              <div><label className={labelCls}>كلمة المرور *</label><input type="password" dir="ltr" className={inputCls} value={staffForm.password} onChange={(e) => setStaffForm({ ...staffForm, password: e.target.value })} required /></div>
              <div>
                <label className={labelCls}>الدور</label>
                <select className={inputCls} value={staffForm.role} onChange={(e) => setStaffForm({ ...staffForm, role: e.target.value })}>
                  <option value="owner">مالك</option>
                  <option value="dispatcher">منسّق</option>
                  <option value="driver">سائق</option>
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={staffSaving} className="flex-1 bg-blue-600 text-white py-2 rounded-lg font-semibold text-sm hover:bg-blue-700 disabled:opacity-50 transition">
                  {staffSaving ? 'جاري الإضافة...' : 'إضافة الموظف'}
                </button>
                <button type="button" onClick={() => setShowStaff(false)} className="flex-1 border py-2 rounded-lg font-semibold text-sm text-gray-600 hover:bg-gray-50 transition">إلغاء</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
